import { createContext, useContext } from "react";
import {
  centralHubLines as baytownHubLines,
  agencyBudgetLines as baytownAgencyLines,
  portfolioContracts as baytownPortfolios,
  systemTotals as baytownSystemTotals,
  fiveYearForecast as baytownFiveYear,
  fiveYearGrandTotal as baytownFiveYearTotal,
  fiveYearAllInTotal as baytownFiveYearAllIn,
  triPortfolioROI as baytownROI,
  operationalPhases as baytownOpPhases,
} from "@/lib/proposalData";
import {
  nhCentralHubLines,
  nhAgencyBudgetLines,
  nhPortfolioContracts,
  nhSystemTotals,
  nhFiveYearForecast,
  nhFiveYearGrandTotal,
  nhFiveYearAllInTotal,
  nhTriPortfolioROI,
  nhOperationalPhases,
} from "@/lib/northHoustonData";

/**
 * DeploymentContext — provides city/partner labels so shared "model" components
 * can render correctly across multiple deployments without hardcoding "Baytown".
 *
 * Default values match Baytown (Proposal 01) so /baytown continues working unchanged.
 * North Houston (and any future deployment) wraps its page in <DeploymentProvider value={...}>
 * to override.
 */

export const baytownDeployment = {
  id: "baytown",
  cityLabel: "Baytown",
  cityShort: "Baytown",
  citySingular: "Baytown",
  fullName: "Baytown Bridge",
  deploymentNumber: "01",
  cityRegion: "Baytown",
  hubLabel: "City of Baytown",
  hubShort: "the City",
  countyLabel: "Harris County",
  corridorLabel: "Gulf Coast LMI corridor",
  college: "Lee College",
  collegeRegion: "Lee College + Workforce Solutions",
  schoolDistrict: "GCCISD",
  schoolDistrictLong: "Goose Creek CISD",
  housingAuthority: "Baytown Housing Authority",
  healthSystem: "Harris Health",
  consolidatedPlan: "2025–2029 Consolidated Plan",
  emailPlaceholder: "you@cityofbaytown.gov",
  orgPlaceholder: "City of Baytown",
  primaryAuthor: "City of Baytown",
  partnerColleges: "Lee College and Workforce Solutions",
  authoringBody: "the City of Baytown, HUD, and GCCISD",
  contextSentence:
    "Baytown, La Porte, Pasadena, Texas City — the Gulf Coast LMI corridor — combine the same demographic pressure Chicago had in 1960 with one critical difference: living-wage industrial and medical jobs are already here.",
  fundingWindowSentence:
    "The City of Baytown's 2025–2029 Consolidated Plan is open. Harris County matches are available. This is the funding window in which a properly-built, fidelity-audited service-connector program can be stood up at scale — and replicated city by city through the rest of the decade.",
  blueprintHeadline:
    "Baytown's blueprint — and the foundation of a new golden age of community service work.",
  closingCardTitle: "Baytown Bridge — Proposal 01 of The Bridge Model",
  closingCardBody:
    "is not a proposal for a new social-services experiment. It is the next chapter of a federally-proven model — carried forward by someone who was personally trained on the original blueprint, ready to be deployed in the city that actually has the jobs and the partners next.",
  demographicLine: "Baytown is 47.2% Hispanic/Latino,",
  demographicLineFull:
    "Hispanic/Latino, Black, White, Asian, immigrant and refugee, urban and rural — Bridge serves the full demographic reality of Baytown and every future deployment city. We mirror the community we are placed in.",
  founderTitleAlt: "George Broughton III, founder of the Baytown Bridge",
  founderBuiltOn: "the Baytown Bridge is built on",
  founderRoleSection: "Role in Baytown Bridge",
  founderQuoteCity:
    "Every day, single mothers and seniors in Baytown fall through the cracks between a college, a clinic, and a housing authority that all exist within five miles of their front door. Baytown Bridge is the human chain that walks them across.",
  strategyOpeningLine:
    "The Baytown Bridge does not chase a single demographic. Most low-income",
  strategyImageAlt: "A family in a Baytown neighborhood",
  seniorPipelineMultigen:
    "Most Baytown low-income households are multigenerational. The same",
  seniorPipelineHandoffs: "HUD, the City of Baytown, and AAA.",
  intakeReportLine:
    "evidence base we report back to the City of Baytown, HUD, and the GCCISD",
  gapAnalysisLead: "Baytown and Harris County already operate strong individual programs.",
  gapAnalysisFollow: "this community needs. Each fills a slice. The Baytown Bridge is the",
  gapAnalysisClose: "The Baytown Bridge is not another fragmented service.",
  chicagoTransitionLine: "the same problem Baytown",
  budgetHubLine: "The City of Baytown acts as Master Administrator — the",
  budgetHubBlock: "City of Baytown",
  implementationOpening: "How Baytown goes from",
  federalOzLine:
    "Baytown is partially within a designated Opportunity Zone. The model's 5-year homeownership ladder activates OZ infrastructure for working families — exactly the framework Secretary Turner stood up at the first-term White House Opportunity & Revitalization Council.",
  federalConnectorLine:
    "One Service Connector simultaneously navigates Lee College trade enrollment, Workforce Solutions childcare, and Baytown Housing Authority placement. The handoff failures HUD case files document every year are eliminated by design.",
  federalPrimaryRecipient:
    "The City of Baytown is prime; three local agencies are sub-recipients; HUD's role is endorsement and pass-through. No federal operational footprint. This is local control, federally enabled.",
  federalScaleLine:
    "Once Baytown is proven, La Porte, Pasadena, Texas City, and the wider Gulf Coast LMI corridor deploy under the same fidelity standards. Twenty cities by end-of-term is realistic, not aspirational.",
  federalTaLine:
    "TA designation that streamlines Baytown deployment and signals to other cities that the model is HUD-validated.",
  galleryHeadline: "Faces of Baytown.",
  galleryLead:
    "Baytown is Black, White, Latino, and Asian. Multi-generational households. Single mothers and aging-in-place seniors. The Bridge serves all of them.",
  galleryCaption: "Baytown · Harris County",
  contactWalkthroughLine:
    "walkthrough of the Baytown Bridge operational model, the Chicago Service",
  contactEmailPlaceholder: "m.hernandez@baytown.org",
  contactOrgPlaceholder: "City of Baytown",
  // ---------- Budget (scoped to deployment) ----------
  budget: {
    hubLines: baytownHubLines,
    agencyLines: baytownAgencyLines,
    portfolios: baytownPortfolios,
    systemTotals: baytownSystemTotals,
    fiveYear: baytownFiveYear,
    fiveYearTotal: baytownFiveYearTotal,
    fiveYearAllIn: baytownFiveYearAllIn,
    roi: baytownROI,
    operationalPhases: baytownOpPhases,
    badge: "Baytown Baseline · 1,500 Households · 3 Spokes × 500",
  },
};

export const northHoustonDeployment = {
  ...baytownDeployment,
  id: "north-houston",
  cityLabel: "the North Houston District",
  cityShort: "the District",
  citySingular: "the District",
  fullName: "Bridge · North Houston",
  deploymentNumber: "02",
  cityRegion: "North Houston District",
  hubLabel: "North Houston District",
  hubShort: "the District",
  countyLabel: "Harris County",
  corridorLabel: "IAH logistics corridor",
  college: "Lone Star College North Harris",
  collegeRegion: "Lone Star North Harris + Workforce Solutions Greater Houston",
  schoolDistrict: "Aldine ISD",
  schoolDistrictLong: "Aldine ISD",
  housingAuthority: "Harris County Housing Authority",
  healthSystem: "Harris Health + Memorial Hermann Northeast",
  consolidatedPlan: "Houston + Harris County CDBG cycles",
  emailPlaceholder: "you@northhouston.org",
  orgPlaceholder: "North Houston District",
  primaryAuthor: "North Houston District",
  partnerColleges: "Lone Star North Harris, HCC North, and Workforce Solutions Greater Houston",
  authoringBody: "the North Houston District Board, the City of Houston, HUD, and Aldine ISD",
  contextSentence:
    "The North Houston District — formerly Greenspoint — anchors the IAH logistics corridor with 20M+ sq ft of warehouse and distribution space, Amazon and FedEx hubs, and a 4-mile gateway to the airport. The same demographic pressure Chicago had in 1960 paired with one critical difference: living-wage logistics, aviation, manufacturing, and allied-health jobs are already here.",
  fundingWindowSentence:
    "Houston's and Harris County's CDBG cycles are open, the District is in active service-plan planning, and federal coordination with HUD Secretary Turner's office is live. This is the funding window in which a properly-built, fidelity-audited service-connector program can be stood up at scale — and replicated district by district through the rest of the decade.",
  blueprintHeadline:
    "The North Houston District blueprint — and the foundation of a new golden age of community service work.",
  closingCardTitle: "Bridge · North Houston — Proposal 02 of The Bridge Model",
  closingCardBody:
    "is not a proposal for a new social-services experiment. It is the next chapter of a federally-proven model — carried forward by someone who was personally trained on the original blueprint, ready to be deployed in the corridor that actually has the jobs and the partners next.",
  demographicLine: "The North Houston District is 62.7% Hispanic/Latino,",
  demographicLineFull:
    "Hispanic/Latino, Black, White, Asian, immigrant and refugee, urban and corridor — Bridge serves the full demographic reality of the North Houston District and every future deployment community. We mirror the community we are placed in.",
  founderTitleAlt: "George Broughton III, Proprietor & Program Designer of The Bridge Model",
  founderBuiltOn: "The Bridge Model is built on",
  founderRoleSection: "Role in The Bridge Model",
  founderQuoteCity:
    "Every day, single mothers and seniors in the North Houston District fall through the cracks between a college, a clinic, and a housing authority that all exist within five miles of their front door. Bridge is the human chain that walks them across.",
  strategyOpeningLine:
    "Bridge does not chase a single demographic. Most low-income",
  strategyImageAlt: "A family in the North Houston District",
  seniorPipelineMultigen:
    "Most North Houston District low-income households are multigenerational. The same",
  seniorPipelineHandoffs:
    "HUD, the North Houston District, and AAA Houston-Harris.",
  intakeReportLine:
    "evidence base we report back to the North Houston District Board, HUD, and Aldine ISD",
  gapAnalysisLead:
    "Houston and Harris County already operate strong individual programs.",
  gapAnalysisFollow:
    "this community needs. Each fills a slice. Bridge · North Houston is the",
  gapAnalysisClose: "Bridge · North Houston is not another fragmented service.",
  chicagoTransitionLine: "the same problem the North Houston District",
  budgetHubLine: "The North Houston District acts as Master Administrator — the",
  budgetHubBlock: "North Houston District",
  implementationOpening: "How the District goes from",
  federalOzLine:
    "The North Houston District overlaps designated Opportunity Zones. The model's 5-year homeownership ladder activates OZ infrastructure for working families — exactly the framework Secretary Turner stood up at the first-term White House Opportunity & Revitalization Council.",
  federalConnectorLine:
    "One Service Connector simultaneously navigates Lone Star North Harris certificate enrollment, Workforce Solutions childcare, and Harris County Housing Authority placement. The handoff failures HUD case files document every year are eliminated by design.",
  federalPrimaryRecipient:
    "The North Houston District is prime convenor; three local agencies are sub-recipients; HUD's role is endorsement and pass-through. No federal operational footprint. This is local control, federally enabled.",
  federalScaleLine:
    "Once North Houston is proven, the wider Houston metropolitan corridor and the Gulf Coast LMI counties deploy under the same fidelity standards. Twenty deployments by end-of-term is realistic, not aspirational.",
  federalTaLine:
    "TA designation that streamlines Proposal 02 and signals to other districts that the model is HUD-validated.",
  galleryHeadline: "Faces of the North Houston District.",
  galleryLead:
    "The District is Hispanic/Latino, Black, White, and Asian. Multi-generational households. Single mothers and aging-in-place seniors. The Bridge serves all of them.",
  galleryCaption: "North Houston District · Harris County",
  contactWalkthroughLine:
    "walkthrough of the Bridge · North Houston operational model, the Chicago Service",
  contactEmailPlaceholder: "you@northhouston.org",
  contactOrgPlaceholder: "North Houston District",
  // ---------- Budget (scoped to deployment 02 · 1,200 households) ----------
  budget: {
    hubLines: nhCentralHubLines,
    agencyLines: nhAgencyBudgetLines,
    portfolios: nhPortfolioContracts,
    systemTotals: nhSystemTotals,
    fiveYear: nhFiveYearForecast,
    fiveYearTotal: nhFiveYearGrandTotal,
    fiveYearAllIn: nhFiveYearAllInTotal,
    roi: nhTriPortfolioROI,
    operationalPhases: nhOperationalPhases,
    badge: "Proposal 02 · North Houston District · 1,200 Households · 3 Spokes × 400 · ~80% of Baytown baseline",
  },
};

export const nationalDeployment = {
  ...baytownDeployment,
  id: "national",
  cityLabel: "every host community",
  cityShort: "the community",
  citySingular: "the host community",
  fullName: "The Bridge Model",
  deploymentNumber: "00",
  cityRegion: "the host community",
  hubLabel: "the host municipality or district",
  hubShort: "the Hub",
  countyLabel: "the host county",
  corridorLabel: "the local workforce corridor",
  college: "the partner community college and workforce board",
  collegeRegion: "the partner community college + workforce board",
  schoolDistrict: "the partner ISD",
  schoolDistrictLong: "the partner school district",
  housingAuthority: "the local housing authority",
  emailPlaceholder: "you@yourcommunity.gov",
  orgPlaceholder: "Your city or district",
  founderTitleAlt: "George Broughton III, Proprietor & Program Designer of The Bridge Model",
  founderBuiltOn: "The Bridge Model is built on",
  founderRoleSection: "Role in The Bridge Model",
  founderQuoteCity:
    "Every day, single mothers and seniors in underserved communities fall through the cracks between a college, a clinic, and a housing authority that all exist within five miles of their front door. The Bridge Model is the human chain that walks them across.",
  contextSentence:
    "Across the country — from Gulf Coast LMI cities to Houston-area districts to rural persistent-poverty counties — communities combine the same demographic pressure Chicago had in 1960 with one critical difference: living-wage industrial, logistics, and medical jobs are increasingly local.",
  fundingWindowSentence:
    "Federal CDBG dollars are flowing now. Houston, Harris County, and dozens of entitlement jurisdictions have open Consolidated Plans. This is the funding window in which a properly-built, fidelity-audited service-connector program can be stood up at scale — and replicated community by community through the rest of the decade.",
  blueprintHeadline:
    "America's blueprint — and the foundation of a new golden age of community service work.",
  closingCardTitle: "The Bridge Model",
  closingCardBody:
    "is not a proposal for a new social-services experiment. It is the next chapter of a federally-proven model — carried forward by someone who was personally trained on the original blueprint, ready to be deployed in the communities that actually have the jobs and the partners next.",
};

const DeploymentContext = createContext(baytownDeployment);

export function DeploymentProvider({ value, children }) {
  return (
    <DeploymentContext.Provider value={value}>
      {children}
    </DeploymentContext.Provider>
  );
}

export function useDeployment() {
  return useContext(DeploymentContext);
}
